import { Layout, Card, Title, Icon, Stack as FoundationStack } from "@introist/react-foundation/v2";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { SectionMenu, SectionMenuItem } from "../../components";
import { AccountsRoot } from "modules/settings/containers/Accounts/AccountsRoot";
import { MicrosoftAccountsRoot } from "modules/settings/containers/Accounts/MicrosoftAccountsRoot";
import styled from "styled-components";

import { useConnectAccount } from "modules/connectedaccounts/hooks/use-connect-account";
import { SharepointIcon, TeamsIcon } from "../../../../assets";
import { GoogleDWDConfig } from "../../containers/GoogleDWD";

const InfoBanner = styled(Card)`
  background-color: #e3f2fd;
  border-left: 3px solid #2196f3;
  padding: 16px;
`;

const InfoLink = styled.span`
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    opacity: 0.8;
  }
`;

export const AccountsRoute = () => {
  const { "*": section } = useParams();
  const navigate = useNavigate();

  const {
    googleAccounts,
    microsoftAccounts,
    slackAccounts,
    teamsAccounts,
    sharepointAccounts,
    microsoftCapabilityAccounts,
    isLoadingConnectedAccounts
  } = useConnectAccount();

  const MicrosoftInfoBanner = () => (
    <InfoBanner>
      <FoundationStack gap="small" alignItems="center">
        <Icon name="info" />
        <Title>
          For new Microsoft connections with multiple capabilities (email, calendar, Teams, SharePoint), use the{" "}
          <InfoLink onClick={() => navigate("../microsoft", { relative: "path" })}>
            Microsoft tab
          </InfoLink>{" "}
          instead.
        </Title>
      </FoundationStack>
    </InfoBanner>
  );

  return (
    <Layout.Group vertical>
      <SectionMenu onItemClick={id => navigate(`../${id}`, { relative: "path" })}>
        <SectionMenuItem
          id="google"
          title="Google Workspace"
          amount={googleAccounts.length}
          active={section === "google"}
          iconName="google"
        />
        <SectionMenuItem
          id="microsoft"
          title="Microsoft"
          amount={microsoftCapabilityAccounts.length}
          active={section === "microsoft"}
          iconName="outlook"
        />
        <SectionMenuItem
          id="outlook"
          title="Microsoft Outlook"
          amount={microsoftAccounts.length}
          active={section === "outlook"}
          iconName="outlook"
        />
        <SectionMenuItem
          id="slack"
          title="Slack"
          amount={slackAccounts.length}
          iconName="slack"
          active={section === "slack"}
        />
        <SectionMenuItem
          id="teams"
          title="Teams"
          amount={teamsAccounts.length}
          icon={<TeamsIcon />}
          active={section === "teams"}
        />
        <SectionMenuItem
          id="sharepoint"
          title="Sharepoint"
          amount={sharepointAccounts.length}
          icon={<SharepointIcon />}
          active={section === "sharepoint"}
        />
      </SectionMenu>
      <Routes>
        <Route path="*" element={<Navigate to="google" replace />} />
        <Route
          path="google"
          element={
            <AccountsRoot
              accounts={googleAccounts}
              iconName="google"
              provider="google"
              title="Google Workspace accounts"
              description="Connect Introist to Google Workspace to send and schedule calendar invites and email messages."
              isLoading={isLoadingConnectedAccounts}
              bottom={<GoogleDWDConfig />}
            />
          }
        />
        <Route
          path="microsoft"
          element={
            <MicrosoftAccountsRoot
              accounts={microsoftCapabilityAccounts}
              description="Connect a single Microsoft account to use for email, calendar, Teams, and SharePoint. Select which capabilities you need when generating the connection link."
              isLoading={isLoadingConnectedAccounts}
            />
          }
        />
        <Route
          path="outlook"
          element={
            <AccountsRoot
              accounts={microsoftAccounts}
              iconName="outlook"
              provider="outlook"
              title="Microsoft Outlook accounts"
              description="Connect Introist to Microsoft Outlook to send email messages and schedule calendar events."
              isLoading={isLoadingConnectedAccounts}
              top={<MicrosoftInfoBanner />}
            />
          }
        />
        <Route
          path="slack"
          element={
            <AccountsRoot
              accounts={slackAccounts}
              iconName="slack"
              provider="slack"
              title="Slack accounts"
              description="Connect Introist to Slack to send channel and personal messages."
              isLoading={isLoadingConnectedAccounts}
            />
          }
        />
        <Route
          path="teams"
          element={
            <AccountsRoot
              accounts={teamsAccounts}
              icon={<TeamsIcon />}
              provider="teams"
              title="Teams accounts"
              description="Connect Introist to Teams to send channel and chat messages."
              isLoading={isLoadingConnectedAccounts}
              top={<MicrosoftInfoBanner />}
            />
          }
        />
        <Route
          path="sharepoint"
          element={
            <AccountsRoot
              accounts={sharepointAccounts}
              icon={<SharepointIcon />}
              provider="sharepoint"
              title="Sharepoint accounts"
              description="Connect Introist to Sharepoint to use data from files."
              isLoading={isLoadingConnectedAccounts}
              disableConnectionLink
              top={<MicrosoftInfoBanner />}
            />
          }
        />
      </Routes>
    </Layout.Group>
  );
};
